'use client';

import { VoteType } from '@prisma/client';
import { motion } from 'framer-motion';
import Image from 'next/image';
import { memo, useMemo } from 'react';
import { SuggestionSeed } from '../../../types/shared';
import { useTourismItemEvents } from '../contexts/TourismGlobalEvents';
import { UniversalVoteButtons } from './shared/UniversalVoteButtons';

interface SuggestionCardProps {
  suggestion: SuggestionSeed;
  myVote: VoteType | null;
  onCardClick: () => void;
  onVote: (voteType: VoteType) => void;
}

export const SuggestionCard = memo(function SuggestionCard({
  suggestion,
  onCardClick,
  myVote,
  onVote,
}: SuggestionCardProps) {
  const ev = useTourismItemEvents(suggestion.id);

  const { likes, dislikes } = useMemo(() => {
    if (ev?.type === 'VOTE_UPDATE' && ev.payload.suggestionId === suggestion.id) {
      return { likes: ev.payload.likes, dislikes: ev.payload.dislikes };
    }
    return { likes: suggestion.initialLikes, dislikes: suggestion.initialDislikes };
  }, [ev, suggestion.id, suggestion.initialLikes, suggestion.initialDislikes]);
  return (
    <motion.div
      layoutId={`suggestion-${suggestion.id}`}
      onClick={onCardClick}
      className="aspect-[4/3] relative rounded-lg overflow-hidden group shadow-lg bg-slate-800 cursor-pointer focus:outline-none focus:ring-2 focus:ring-sky-500"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div
        className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <UniversalVoteButtons
          likes={likes}
          dislikes={dislikes}
          myVote={myVote}
          onVote={onVote}
          variant="card"
        />
      </div>

      <Image
        src={suggestion.imageUrl}
        alt={suggestion.title}
        fill
        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
        className="object-cover transition-transform duration-300 group-hover:scale-105"
        loading="lazy"
        quality={75}
      />

      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
        <h3 className="font-bold text-lg text-white truncate">{suggestion.title}</h3>
        <p className="text-sm text-slate-300">от {suggestion.author.name}</p>
      </div>
    </motion.div>
  );
});
